import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { format } from 'date-fns';
import { formatDate, downloadFile } from '../../../../../utils/func';
import { apiGetQuoteReports } from '../../../../../services/quotes';
import PrimaryButton from '../../../buttons/PrimaryButton';
import SecondaryButton from '../../../buttons/SecondaryButton';
import DatePicker from '../../../form_elements/DatePicker';
import Checkbox from '../../../form_elements/Checkbox';
import {
  ModalContainer,
  ModalTitlesContainer,
  ModalTitle,
  ModalSubTitle,
  DownloadModalBody,
  DatesContainer,
  CategoriesContainer,
  CategoryContainer,
  CategoryTitle,
  BoxContainer,
  BoxInfo,
  ButtonGroup,
  DatePickerWrapper,
  ErrorMessage,
} from './css';

const NUMBER_OF_DAYS = 60;
const BOX_CONTAINER_MAX_HEIGHT = '145px';

const LABEL_COLUMNS_TRANSLATION_KEY = {
  quote: 'Quote',
  shipper: 'Shipper',
  consignee: 'Consignee',
  status: 'Status',
  service: 'Service',
  created: 'DateCreated',
  expiry: 'Expiry',
  pallets: 'PalletsDelivery',
  pieces: 'PiecesDelivery',
  weight: 'Weight',
};

const DEFAULT_COLUMNS = [
  'quote',
  'shipper',
  'consignee',
  'service',
  'created',
  'expiry',
  'pieces',
  'weight',
];

const DownloadModal = ({ accountNumbers, closeModal }) => {
  const today = new Date();
  const pastDays = new Date((new Date()).getTime() - (NUMBER_OF_DAYS * 24 * 60 * 60 * 1000));

  const [filterFromDate, setFilterFromDate] = useState(formatDate(pastDays));
  const [filterToDate, setFilterToDate] = useState(formatDate(today));
  const [filterAccountNumbers, setFilterAccountNumbers] = useState(accountNumbers || []);
  const [filterColumns, setFilterColumns] = useState(DEFAULT_COLUMNS);
  const [dateError, setDateError] = useState(null);

  const accountNumbersOptions = (accountNumbers || []).map((acc) => ({
    label: acc,
    value: acc,
  }));

  const columnsOptions = DEFAULT_COLUMNS.map((column) => ({
    label: t(`Column${LABEL_COLUMNS_TRANSLATION_KEY[column]}`),
    value: column,
  }));

  useEffect(() => {
    setFilterAccountNumbers(accountNumbers || []);
  }, [accountNumbers]);

  const validateDates = () => {
    if (!filterFromDate || !filterToDate) {
      setDateError(t('DateRequired'));

      return false;
    }

    const from = new Date(filterFromDate);
    const to = new Date(filterToDate);
    if (from > to) {
      setDateError(t('FromDateMustBeBeforeToDate'));

      return false;
    }

    setDateError(null);

    return true;
  };

  const handleFromDateChange = (value) => {
    setFilterFromDate(value);
    setDateError(null);
  };

  const handleToDateChange = (value) => {
    setFilterToDate(value);
    setDateError(null);
  };

  const handleAccountNumbersChange = (values) => {
    setFilterAccountNumbers(values);
  };

  const handleSelectAllAccountNumbers = (event) => {
    if (event.target.checked) {
      setFilterAccountNumbers(accountNumbers || []);
    } else {
      setFilterAccountNumbers([]);
    }
  };

  const allAccountNumbersSelected = accountNumbers
    && accountNumbers.length > 0
    && filterAccountNumbers.length === accountNumbers.length;

  const handleColumnsChange = (values) => {
    setFilterColumns(values);
  };

  const handleDownloadSubmit = (event) => {
    event.preventDefault();

    if (!validateDates()) {
      return;
    }

    const downloadAccountNumbers = filterAccountNumbers.filter((n) => n !== '');
    if (!downloadAccountNumbers || downloadAccountNumbers.length === 0) {
      return;
    }

    const fromDateFormatted = format(new Date(filterFromDate), 'MMMM d, yyyy').split('+')[0];
    const toDateFormatted = format(new Date(filterToDate), 'MMMM d, yyyy').split('+')[0];

    apiGetQuoteReports(downloadAccountNumbers, filterColumns, fromDateFormatted, toDateFormatted).then((res) => {
      if (res.status === 200) {
        const { fileName, mimeType, fileDataAsBase64 } = res.data;
        const fileType = fileName.includes('.csv') ? '' : '.csv';
        downloadFile({ dataAsBase64: fileDataAsBase64, mimeType, filename: fileName + fileType });
        closeModal();
      }
    });
  };

  const isValid = filterFromDate && filterToDate && filterAccountNumbers.length > 0 && filterColumns.length > 0 && !dateError;

  return (
    <form onSubmit={handleDownloadSubmit}>
      <ModalContainer>
        <ModalTitlesContainer>
          <ModalTitle>{t('DownloadQuoteReportTitle')}</ModalTitle>
          <ModalSubTitle>{t('DownloadQuoteReportDescription')}</ModalSubTitle>
        </ModalTitlesContainer>

        <DownloadModalBody>
          <DatesContainer>
            <DatePickerWrapper>
              <DatePicker
                label={t('FromDate')}
                input={{
                  value: filterFromDate,
                  onChange: handleFromDateChange,
                }}
                maxDate={today}
                meta={{}}
              />
            </DatePickerWrapper>
            <DatePickerWrapper>
              <DatePicker
                label={t('ToDate')}
                input={{
                  value: filterToDate,
                  onChange: handleToDateChange,
                }}
                maxDate={today}
                meta={{}}
              />
            </DatePickerWrapper>
          </DatesContainer>
          {dateError && <ErrorMessage>{dateError}</ErrorMessage>}

          <CategoriesContainer>
            <CategoryContainer>
              <CategoryTitle>{t('BillingAccount')}</CategoryTitle>
              <BoxContainer maxHeight={BOX_CONTAINER_MAX_HEIGHT}>
                <BoxInfo>
                  <Checkbox
                    input={{
                      value: allAccountNumbersSelected,
                      onChange: handleSelectAllAccountNumbers,
                    }}
                    label={t('SelectAll')}
                  />
                  <Checkbox
                    input={{
                      value: filterAccountNumbers,
                      onChange: handleAccountNumbersChange,
                    }}
                    options={accountNumbersOptions}
                    multiple
                    meta={{}}
                  />
                </BoxInfo>
              </BoxContainer>
            </CategoryContainer>

            <CategoryContainer>
              <CategoryTitle>{t('Columns')}</CategoryTitle>
              <BoxContainer maxHeight={BOX_CONTAINER_MAX_HEIGHT}>
                <BoxInfo>
                  <Checkbox
                    input={{
                      value: filterColumns,
                      onChange: handleColumnsChange,
                    }}
                    options={columnsOptions}
                    multiple
                    meta={{}}
                  />
                </BoxInfo>
              </BoxContainer>
            </CategoryContainer>
          </CategoriesContainer>
        </DownloadModalBody>

        <ButtonGroup>
          <SecondaryButton
            label={t('Cancel')}
            onClick={closeModal}
            showArrow={false}
          />
          <PrimaryButton
            type="submit"
            label={t('Download')}
            disabled={!isValid}
          />
        </ButtonGroup>
      </ModalContainer>
    </form>
  );
};

DownloadModal.propTypes = {
  accountNumbers: PropTypes.array,
  closeModal: PropTypes.func.isRequired,
};

export default DownloadModal;
